/**
 * Shared settings configuration — single source of truth for all groups and items.
 * Used by SettingsIndex, SettingsGroupPage, and the Sidebar.
 *
 * Each item declares the permissions required to access it (`requiredPermissions`).
 * A user can access an item if they hold at least one of the listed permissions,
 * or if the list is empty (personal items; always visible to any signed-in user).
 * Groups are visible when at least one of their items is accessible.
 */

import type { PermissionAction } from '../../../types/prioritization/permissions';
import { isFeatureEnabled, FEATURE_FLAGS, type FeatureFlagValue } from '../../../lib/featureFlags';
import {
  Zap,
  Users,
  Layers,
  Shield,
  Key,
  FolderTree,
  Tags,
  Plug,
  GitBranch,
  LayoutTemplate,
  UserCog,
  Wrench,
  Tag,
  LayoutGrid,
  ToggleLeft,
  BarChart3,
  UserCircle,
  Building2,
  Inbox,
  FileText,
  Fingerprint,
  CheckCircle2,
  Lock,
  Activity,
  Database,
  FlaskConical,
  SlidersHorizontal,
  Archive,
  Ruler,
  Bot,
  PieChart,
  CalendarDays,
  ArrowLeftRight,
  AlertTriangle,
  Link2,
  Scale,
  TrendingUp,
  Package,
  BookOpen,
  Users2,
  ScanEye,
  Settings2,
  Cpu,
  Radar,
  Copy,
  Rocket,
  Shapes,
} from 'lucide-react';

export interface SettingsItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  /**
   * Optional subsection label within a group index page (e.g. "Sandbox").
   */
  subsection?: string;
  /**
   * Permissions required to access this item. A user can open the page if they
   * hold at least one of the listed permissions. An empty/undefined list means
   * "always accessible" (used for personal items like My Profile).
   */
  requiredPermissions?: PermissionAction[];
  /**
   * When set, the workspace module must be enabled for this settings item to appear.
   */
  requiredModule?: string;
  /**
   * When set (with requiredModule), the individual feature flag must also be enabled.
   */
  requiredFeatureFlag?: FeatureFlagValue;
  /**
   * Platform/system admin only. Overrides requiredPermissions — item is hidden
   * unless the user is a platform/system admin.
   */
  adminOnly?: boolean;
  /**
   * Gated but not drawn: the route guard checks the page's permissions like any
   * other item, but the index pages and the sidebar leave it out because it is
   * reached from its hub (the Agents page, the Users page). A page under
   * /prioritization/settings with no entry here is not gated at all.
   */
  unlisted?: boolean;
}

export interface SettingsGroup {
  key: string;
  title: string;
  description: string;
  indexHref: string;
  icon: React.ComponentType<{ className?: string }>;
  items: SettingsItem[];
  adminOnly?: boolean;
  /**
   * Sidebar visibility beyond per-item permission filtering.
   * - always: show whenever the user has accessible items
   * - not-small-space: hide in a small space, personal or team (default for workspace groups)
   * - team-or-not-small-space: show in team spaces and in organizational ones; hide in personal
   */
  sidebarVisibility?: 'always' | 'not-small-space' | 'team-or-not-small-space';
}

// ── Named agent settings pages ────────────────────────────────────────────────
// Each is /prioritization/settings/<slug>, reached from the Agents page.
// cassandra-agent only redirects to fracture-agent; it is gated the same so the
// redirect is never a way around the guard.

const NAMED_AGENT_SETTINGS_PAGES: SettingsItem[] = (
  [
    ['atc-agent', 'Air Traffic Controller'],
    ['value-estimator-agent', 'Value Estimator'],
    ['twins-agent', 'Twins'],
    ['scribe-agent', 'Scribe'],
    ['harry-agent', 'Harry'],
    ['wes-agent', 'Wes'],
    ['nexus-agent', 'Nexus'],
    ['fracture-agent', 'Fracture'],
    ['cassandra-agent', 'Fracture (old address)'],
    ['compass-agent', 'Compass'],
    ['keystone-agent', 'Keystone'],
    ['meridian-agent', 'Meridian'],
    ['condition-watch-agent', 'Condition Watch'],
    ['beacon-agent', 'Beacon'],
    ['review-due-agent', 'Review Due'],
  ] as const
).map(([slug, name]): SettingsItem => ({
  name,
  href: `/prioritization/settings/${slug}`,
  icon: Bot,
  description: `${name} agent settings`,
  requiredPermissions: ['admin.settings'],
  unlisted: true,
}));

// ── Personal (not workspace-scoped) ───────────────────────────────────────────

export const PERSONAL_GROUP: SettingsGroup = {
  key: 'my-account',
  title: 'My Account',
  description: 'Personal preferences — visible only to you, not applied workspace-wide.',
  indexHref: '/prioritization/settings/my-account',
  icon: UserCircle,
  sidebarVisibility: 'always',
  items: [
    {
      name: 'My Profile',
      href: '/prioritization/settings/my-profile',
      icon: UserCircle,
      description: 'Your title, seniority, expertise areas, and attributes used in analysis',
      requiredPermissions: [],
    },
    {
      name: 'My Connectors',
      href: '/prioritization/settings/my-connectors',
      icon: Plug,
      description: 'Personal AI keys plus Miro/Jira credentials — overrides workspace defaults where supported',
      requiredPermissions: [],
    },
    {
      name: 'My roles',
      href: '/prioritization/settings/my-roles',
      icon: UserCog,
      description: 'Feature the roles you work in: your menu and your home show these roles’ work',
      requiredPermissions: [],
    },
    {
      name: 'Change Password',
      href: '/prioritization/settings/change-password',
      icon: Lock,
      description: 'Update the password you use to sign in',
      requiredPermissions: [],
    },
    {
      name: 'Feature Flags',
      href: '/prioritization/settings/features',
      icon: Wrench,
      description: 'Personal on/off toggles for features and navigation — your settings only affect you',
      requiredPermissions: [],
    },
  ],
};

// ── Workspace groups ───────────────────────────────────────────────────────────

export const WORKSPACE_GROUPS: SettingsGroup[] = [
  {
    key: 'workspace-setup',
    title: 'Workspace Setup',
    description: 'Core workspace structure — spaces, modules, and feature availability.',
    indexHref: '/prioritization/settings/workspace-setup',
    icon: Building2,
    sidebarVisibility: 'not-small-space',
    items: [
      { name: 'Spaces', href: '/prioritization/settings/spaces', icon: Layers, description: 'Manage personal, team, and org-wide collaboration spaces — control who can see and do what within each space', requiredPermissions: [] },
      { name: 'Modules', href: '/prioritization/settings/modules', icon: ToggleLeft, description: 'Turn platform modules on or off for this workspace', requiredPermissions: ['admin.settings'] },
      // Moved out of Developer & Admin (owner, 2026-10-16); shown only while the `input_rounds` module is on.
      { name: 'Input rounds', href: '/prioritization/input-rounds', icon: Users2, description: 'Crowd input rounds — collect structured input on items from stakeholders before a prioritization cycle', requiredPermissions: ['admin.settings'], requiredModule: 'input_rounds' },
      { name: 'Import / Export Settings', href: '/prioritization/settings/settings-transfer', icon: Copy, description: 'Copy this workspace’s configuration to a file and paste it into a new workspace — choose which settings to bring over', requiredPermissions: ['admin.settings'] },
    ],
  },
  {
    key: 'people-agents',
    title: 'People & Agents',
    description: 'Workspace members and AI agents — users plus guide, item work, intelligence, Voices, and assists.',
    indexHref: '/prioritization/settings/people-agents',
    icon: Users,
    sidebarVisibility: 'team-or-not-small-space',
    items: [
      { name: 'Users', href: '/prioritization/settings/users', icon: Users, description: 'Manage workspace users and their access', requiredPermissions: ['admin.users'] },
      { name: 'Experience Profiles', href: '/prioritization/settings/experience-profiles', icon: Users, description: 'Full product or Guided (one home page, one thing at a time) — per member, honoured on every device', requiredPermissions: ['admin.users'] },
      { name: 'Agents', href: '/prioritization/settings/agents', icon: Bot, description: 'Workspace agents — guide, item work, intelligence, Voices, and AI Assists', requiredPermissions: ['admin.settings'] },
      // Reached from the Users and Agents pages; gated like them, not listed again.
      { name: 'Invitations', href: '/prioritization/settings/invites', icon: Users, description: 'Pending, used, and expired invitations to this workspace', requiredPermissions: ['admin.users'], unlisted: true },
      { name: 'AI Assists', href: '/prioritization/settings/ai-assists', icon: Bot, description: 'Which AI assists are on in this workspace', requiredPermissions: ['admin.settings'], unlisted: true },
      { name: 'Voices', href: '/prioritization/settings/voices', icon: Bot, description: 'Agents that speak for a body of people', requiredPermissions: ['admin.settings'], unlisted: true },
      { name: 'Custom Agents', href: '/prioritization/settings/custom-agents', icon: Bot, description: 'Agents this workspace built from a type', requiredPermissions: ['admin.settings'], unlisted: true },
      ...NAMED_AGENT_SETTINGS_PAGES,
    ],
  },
  {
    key: 'groups-access',
    title: 'Groups & Access',
    description: 'Groups, roles, permissions, policies, and org structure.',
    indexHref: '/prioritization/settings/groups-access',
    icon: Shield,
    sidebarVisibility: 'team-or-not-small-space',
    items: [
      { name: 'Groups', href: '/prioritization/settings/groups', icon: Layers, description: 'Organize users into groups for sessions and permissions', requiredPermissions: ['admin.groups'] },
      { name: 'Roles', href: '/prioritization/settings/roles', icon: Shield, description: 'Configure roles and the permissions they carry', requiredPermissions: ['admin.roles'] },
      { name: 'Role Packages', href: '/prioritization/settings/role-packages', icon: Package, description: 'Curated multi-role bundles — assign a named set of job roles to a user or group in one action', requiredPermissions: ['admin.roles'] },
      { name: 'Permissions', href: '/prioritization/settings/permissions', icon: Key, description: 'Reference all permissions and which roles hold them', requiredPermissions: ['admin.roles', 'admin.policies'] },
      { name: 'Attribute Permissions', href: '/prioritization/settings/attribute-permissions', icon: Lock, description: 'Per-attribute governance: who can define ranges, propose values, and approve scores', requiredPermissions: ['admin.settings', 'attributes.assign_owner'], requiredModule: 'attributes' },
      { name: 'Policies', href: '/prioritization/settings/policies', icon: FileText, description: 'Author and manage access control policies by role, scope, and state', requiredPermissions: ['admin.policies'] },
      { name: 'Org Hierarchy', href: '/prioritization/settings/org-hierarchy', icon: FolderTree, description: 'Define organizational structure for scope and reporting', requiredPermissions: ['admin.settings'], requiredModule: 'org_hierarchy' },
      { name: 'Product Hierarchy', href: '/prioritization/settings/product-hierarchy', icon: Layers, description: 'Model the portfolio as a tree — product lines, products, components — and place items in it', requiredPermissions: ['admin.settings'], requiredModule: 'product_hierarchy', requiredFeatureFlag: FEATURE_FLAGS.PRODUCT_HIERARCHY },
    ],
  },
  {
    key: 'workflows-templates',
    title: 'Workflows & Templates',
    description: 'Runtime orchestration policy and initialization blueprints.',
    indexHref: '/prioritization/settings/workflows-templates',
    icon: GitBranch,
    sidebarVisibility: 'not-small-space',
    items: [
      { name: 'Workflows', href: '/prioritization/settings/workflows', icon: GitBranch, description: 'Runtime orchestration policy: session modes, write-back, result resolution, Catchball', requiredPermissions: ['admin.settings', 'workflow.edit'] },
      { name: 'Templates', href: '/prioritization/settings/templates', icon: LayoutTemplate, description: 'Initialization blueprints — seed attributes, participants, and groups at cycle creation', requiredPermissions: ['admin.settings', 'workflow.manage_templates'] },
      { name: 'Automations', href: '/prioritization/settings/automations', icon: Zap, description: 'Rules that take the next step for you when something changes: notify people, assign an owner, move, tag, change state, run an agent', requiredPermissions: ['admin.settings'] },
    ],
  },
  {
    key: 'content',
    title: 'Content & Structure',
    description: 'How items are defined, categorized, and submitted across the workspace.',
    indexHref: '/prioritization/settings/content',
    icon: Tags,
    sidebarVisibility: 'not-small-space',
    items: [
      { name: 'Attributes', href: '/prioritization/settings/attributes', icon: Tags, description: 'Scoring attributes for prioritization sessions', requiredPermissions: ['admin.settings', 'attributes.define'], requiredModule: 'attributes' },
      { name: 'Value Scales', href: '/prioritization/settings/value-scales', icon: Ruler, description: 'Browse and open measurement scales for every scoring attribute', requiredPermissions: ['admin.settings', 'values.define_scales'], requiredModule: 'attributes' },
      { name: 'Starting Points', href: '/prioritization/settings/configurations', icon: LayoutTemplate, description: 'Show/hide pre-built starting points in the launcher; create custom configurations', requiredPermissions: ['admin.settings'] },
      { name: 'Tracks', href: '/prioritization/settings/tracks', icon: Layers, description: 'Define which Tracks (category × altitude) are available when cycles are created — use the default Category by Altitude or build a custom list', requiredPermissions: ['admin.settings'] },
      { name: 'Cycles, Tracks & Sessions', href: '/prioritization/settings/planning', icon: CalendarDays, description: 'Create, rename, archive, and delete cycles, Tracks, and sessions', requiredPermissions: ['admin.settings'] },
      { name: 'Custom Fields', href: '/prioritization/settings/custom-fields', icon: SlidersHorizontal, description: 'Define custom fields for items, roadmap entries, and other objects — toggle each field on or off per workspace', requiredPermissions: ['admin.settings'], requiredModule: 'attributes' },
      { name: 'Readiness', href: '/prioritization/settings/readiness', icon: CheckCircle2, description: 'What every item must have, and what an item needs to be ranked — the workspace set, or one a cycle or track picks', requiredPermissions: ['admin.settings'] },
      { name: 'Item Categories', href: '/prioritization/settings/categories', icon: LayoutGrid, description: 'Toggle built-in item categories and create custom ones with altitude quizzes', requiredPermissions: ['admin.settings'] },
      { name: 'Idea Sources', href: '/prioritization/settings/sources', icon: Tag, description: 'Customize the source labels shown on the idea submission form', requiredPermissions: ['admin.settings'] },
      { name: 'Queues', href: '/prioritization/settings/queues', icon: Inbox, description: 'Configure which org units accept item submissions as queues', requiredPermissions: ['admin.settings'] },
    ],
  },
  {
    key: 'patterns',
    title: 'Patterns',
    description: 'How things usually unfold once you know what kind of thing an item is — one list per way of seeing it.',
    indexHref: '/prioritization/settings/patterns',
    icon: Shapes,
    sidebarVisibility: 'not-small-space',
    items: [
      {
        name: 'Type Lists & Patterns',
        href: '/prioritization/settings/type-patterns',
        icon: Layers,
        description:
          'Each department’s way of classifying items, and the pattern each type carries — certainty, shape, workload, bar',
        // Open to members: a list's owners (the people under its org unit) edit it without
        // admin rights. Each list draws its edit controls only for owners and admins.
        requiredPermissions: [],
      },
      { name: 'Work Types', href: '/prioritization/settings/work-types', icon: Wrench, description: 'Built-in and custom work type catalog — approach kind, uncertainty multiplier, and intake metadata per workspace', requiredPermissions: ['admin.settings'], requiredModule: 'execution_recipe', requiredFeatureFlag: FEATURE_FLAGS.EXECUTION_RECIPE },
      { name: 'Launch Types', href: '/prioritization/settings/launch-types', icon: Rocket, description: 'Marketing launch classification on items — product, feature, packaging, partner GTM (not Workload Templates)', requiredPermissions: ['admin.settings'] },
      { name: 'Investment Types', href: '/prioritization/settings/investment-types', icon: PieChart, description: 'Financial investment classification on items — growth, maintain, platform, risk (reportable portfolio lens)', requiredPermissions: ['admin.settings'] },
    ],
  },
  {
    key: 'planning-engine',
    title: 'Planning',
    description: 'The teams that do the work, and how plans are set up. How work is classified lives under Patterns.',
    indexHref: '/prioritization/settings/planning-engine',
    icon: Cpu,
    sidebarVisibility: 'not-small-space',
    items: [
      {
        name: 'Resource Types',
        href: '/prioritization/settings/resource-types',
        icon: Users,
        description: 'Define the team and skill types used in capacity modelling, work units, and priority assignments',
        requiredPermissions: ['admin.settings'],
      },
      {
        name: 'Planning Defaults',
        href: '/prioritization/settings/planning-defaults',
        icon: Settings2,
        description:
          'Workspace defaults for new scenarios, plus Item Effort hours by altitude / Work Size for viability planning',
        requiredPermissions: ['admin.settings'],
      },
      {
        name: 'Workload Templates',
        href: '/prioritization/settings/workload-templates',
        icon: Rocket,
        description:
          'Hours by team, week by week around a date — a type’s pattern can start from one',
        requiredPermissions: ['admin.settings'],
        requiredModule: 'time_phased_planning',
        requiredFeatureFlag: FEATURE_FLAGS.TIME_PHASED_PLANNING,
      },
      {
        name: 'Predictability Profiles',
        href: '/prioritization/settings/predictability-profiles',
        icon: SlidersHorizontal,
        description:
          'Slack, confidence ceiling, and commitment firmness for experimental vs solid calendar work',
        requiredPermissions: ['admin.settings'],
        requiredModule: 'time_phased_planning',
        requiredFeatureFlag: FEATURE_FLAGS.TIME_PHASED_PLANNING,
      },
      { name: 'Plan of Record', href: '/prioritization/settings/plan-of-record', icon: BookOpen, description: 'Choose which pages appear in each Plan of Record — dashboard, narrative sections, and change log', requiredPermissions: ['admin.settings'] },
    ],
  },
  {
    key: 'integrations-sync',
    title: 'Integrations & Sync',
    description: 'Connect external systems, map entities, monitor sync health, and resolve conflicts.',
    indexHref: '/prioritization/settings/integrations-sync',
    icon: ArrowLeftRight,
    sidebarVisibility: 'not-small-space',
    items: [
      { name: 'Connectors', href: '/prioritization/settings/connectors', icon: Plug, description: 'External integrations and data connectors', requiredPermissions: ['admin.settings'] },
      { name: 'Field Mappings', href: '/prioritization/settings/field-mappings', icon: ArrowLeftRight, description: 'Named mapping sets for connected systems (Jira, Miro, Slack, …). CSV profiles live under Spreadsheet sync.', requiredPermissions: ['admin.settings'] },
      { name: 'Spreadsheet sync', href: '/prioritization/settings/csv-citizenship', icon: ArrowLeftRight, description: 'Import and export items as a spreadsheet: saved column mappings, run now or on a schedule, and run history', requiredPermissions: ['admin.settings'] },
      { name: 'Entity Linker', href: '/prioritization/settings/entity-linker', icon: Link2, description: 'Map existing priorities.ai entities to their external system counterparts to enable inbound sync', requiredPermissions: ['admin.settings'] },
      { name: 'Sync Health', href: '/prioritization/settings/sync-health', icon: Activity, description: 'Per-connector health, SLA monitoring, and no-code trust-rule configuration', requiredPermissions: ['admin.settings'] },
      { name: 'Sync Event Log', href: '/prioritization/settings/sync-log', icon: ArrowLeftRight, description: 'Append-only audit log of all sync events from priorities.ai and connected systems', requiredPermissions: ['admin.settings'] },
      { name: 'Conflict Queue', href: '/prioritization/settings/conflict-queue', icon: AlertTriangle, description: 'Review and resolve change events where an external system tried to modify a priorities.ai-owned field', requiredPermissions: ['admin.settings'] },
      { name: 'Ambient Runtime', href: '/prioritization/settings/ambient-runtime', icon: Radar, description: 'Background jobs, run history, deliveries, and the workspace channel policy for the ambient layer', requiredPermissions: ['admin.settings'], requiredModule: 'ambient_runtime', requiredFeatureFlag: FEATURE_FLAGS.AMBIENT_RUNTIME },
      { name: 'Excursions', href: '/prioritization/settings/excursions', icon: ArrowLeftRight, description: 'Round-trip work with external boards — send a collection to Miro, evolve it there, and bring the meaning home', requiredPermissions: ['admin.settings'], requiredModule: 'semantic_connectors', requiredFeatureFlag: FEATURE_FLAGS.SEMANTIC_CONNECTORS },
    ],
  },
  {
    key: 'reports',
    title: 'Reports',
    description: 'Report configuration, onboarding analytics, and accountability trails.',
    indexHref: '/prioritization/settings/reports',
    icon: BarChart3,
    sidebarVisibility: 'not-small-space',
    items: [
      { name: 'Reports', href: '/prioritization/settings/reports-config', icon: BarChart3, description: 'Configure which reports are shown and which are required on the Reports page', requiredPermissions: ['admin.settings'], requiredModule: 'reports' },
      { name: 'Onboarding Funnel', href: '/prioritization/settings/onboarding-funnel', icon: TrendingUp, description: 'Track which first-value milestones each teammate has completed — see where people drop off', requiredPermissions: ['admin.settings'] },
      { name: 'Experience Audit', href: '/prioritization/settings/experience-audit', icon: ScanEye, description: 'Reference every shipped role default — navigation emphasis, home dashboard widgets, and composed layouts', requiredPermissions: ['admin.settings'] },
      { name: 'Classification Report', href: '/prioritization/settings/classification-report', icon: BarChart3, description: 'History of all category and altitude classification proposals — accepted, overridden, superseded, and dismissed', requiredPermissions: ['admin.settings'] },
      { name: 'Accountability Trail', href: '/prioritization/settings/accountability', icon: Scale, description: 'Who made which priority decisions and when — searchable timeline from governance events', requiredPermissions: ['admin.settings'] },
      { name: 'Audit Log', href: '/prioritization/settings/audit-log', icon: FileText, description: 'Full workspace change history — governance decisions, item changes, and workflow events. Export to CSV.', requiredPermissions: ['admin.settings'] },
      { name: 'Backlog Health', href: '/prioritization/settings/backlog-health', icon: Activity, description: 'How healthy the backlog is — stale, unowned, and unready items', requiredPermissions: ['admin.settings'], requiredModule: 'backlog_health', unlisted: true },
      { name: 'Sector Map', href: '/prioritization/settings/sandbox/sector-map', icon: PieChart, description: 'Effort-weighted backlog visualization — each slice is proportional to altitude-based work units, not item count', requiredPermissions: ['admin.settings'] },
    ],
  },
  {
    key: 'developer',
    title: 'Developer & Admin',
    description: 'Platform administration, diagnostics, and experimental tools.',
    indexHref: '/prioritization/settings/developer',
    icon: Wrench,
    adminOnly: true,
    sidebarVisibility: 'not-small-space',
    items: [
      { name: 'Workspace Plan', href: '/prioritization/settings/plan', icon: Package, description: 'Current commercial tier, entitlements, and usage limits — read from workspace_plans', requiredPermissions: ['admin.settings'] },
      { name: 'Workspace Status', href: '/prioritization/settings/status', icon: Activity, description: 'Live health checks for all backing services — integrations, LLM, database', requiredPermissions: ['admin.settings'] },
      { name: 'Identity', href: '/prioritization/settings/identity', icon: Fingerprint, description: 'Directory sync, SCIM, LDAP, and org structure import', requiredPermissions: ['admin.users', 'admin.settings'] },
      { name: 'API Keys', href: '/prioritization/settings/api', icon: Key, description: 'Programmatic access keys for integrations and automation', requiredPermissions: ['admin.settings'] },
      { name: 'Data Diagnostics', href: '/prioritization/settings/diagnostics', icon: Database, description: 'Identify data inconsistencies between seed data, user data, and Supabase', requiredPermissions: ['admin.settings'] },
      { name: 'Authorization Diagnostics', href: '/prioritization/settings/authorization', icon: FlaskConical, description: 'Interactive walkthrough of the three-layer authorization system', requiredPermissions: ['admin.settings'] },
      { name: 'Archived Items', href: '/prioritization/settings/archived-items', icon: Archive, description: 'Every archived item, what happened to it, and a take-back where one is allowed', requiredPermissions: ['admin.settings', 'items.edit'] },
      { name: 'Platform Admin', href: '/platform-admin', icon: Shield, description: 'Platform-level administration and super-admin tools', adminOnly: true, requiredPermissions: ['admin.policies'] },
    ],
  },
];

export const ALL_SETTINGS_GROUPS: SettingsGroup[] = [PERSONAL_GROUP, ...WORKSPACE_GROUPS];

/** Look up a group by its key */
export function getSettingsGroup(key: string): SettingsGroup | undefined {
  return ALL_SETTINGS_GROUPS.find((g) => g.key === key);
}

export interface SettingsPathMatch {
  group: SettingsGroup;
  item?: SettingsItem;
}

/**
 * Resolve a pathname to the SettingsGroup/SettingsItem it represents — what
 * SettingsRouteGuard gates. Returns null when the path has no entry here (a
 * dynamic detail page like /settings/users/:userId/permissions falls back to
 * the nearest item match via startsWith).
 */
export function matchSettingsPath(pathname: string): SettingsPathMatch | null {
  for (const group of ALL_SETTINGS_GROUPS) {
    for (const item of group.items) {
      if (pathname === item.href || pathname.startsWith(item.href + '/')) {
        return { group, item };
      }
    }
    if (pathname === group.indexHref) {
      return { group };
    }
  }
  return null;
}

/** Given a settings path, find which group it belongs to */
export function getGroupForPath(pathname: string): SettingsGroup | undefined {
  const byIndex = ALL_SETTINGS_GROUPS.find((g) => pathname === g.indexHref);
  if (byIndex) return byIndex;
  return ALL_SETTINGS_GROUPS.find((g) => g.items.some((item) => pathname.startsWith(item.href)));
}

/** Group settings items by optional subsection for group index pages. */
export function groupSettingsItemsBySubsection(items: SettingsItem[]): { subsection?: string; items: SettingsItem[] }[] {
  const sections: { subsection?: string; items: SettingsItem[] }[] = [];
  for (const item of items) {
    const last = sections[sections.length - 1];
    if (last && last.subsection === item.subsection) {
      last.items.push(item);
    } else {
      sections.push({ subsection: item.subsection, items: [item] });
    }
  }
  return sections;
}

/** Should a settings group appear in the sidebar for this workspace mode? */
export function isSettingsGroupVisibleInSidebar(
  group: SettingsGroup,
  opts: { isSmallSpace: boolean; isTeamSpace: boolean },
): boolean {
  const mode = group.sidebarVisibility ?? 'not-small-space';
  if (mode === 'always') return true;
  if (mode === 'team-or-not-small-space') {
    return opts.isTeamSpace || !opts.isSmallSpace;
  }
  return !opts.isSmallSpace;
}

// ── Visibility helpers ────────────────────────────────────────────────────────

export type CanAnyPermission = (actions: PermissionAction[]) => boolean;

export interface SettingsAccessContext {
  canAny: CanAnyPermission;
  isPlatformAdmin: boolean;
  isModuleEnabled?: (moduleKey: string) => boolean;
}

export function isSettingsItemVisible(item: SettingsItem, ctx: SettingsAccessContext): boolean {
  if (item.adminOnly && !ctx.isPlatformAdmin) return false;
  if (item.requiredModule && ctx.isModuleEnabled && !ctx.isModuleEnabled(item.requiredModule)) return false;
  if (item.requiredFeatureFlag && !isFeatureEnabled(item.requiredFeatureFlag)) return false;
  const required = item.requiredPermissions;
  if (!required || required.length === 0) return true;
  return ctx.canAny(required);
}

/** The items to draw on an index page or in the sidebar: visible and listed. */
export function filterVisibleSettingsItems(items: SettingsItem[], ctx: SettingsAccessContext): SettingsItem[] {
  return items.filter((item) => !item.unlisted && isSettingsItemVisible(item, ctx));
}

export function filterVisibleSettingsGroups(groups: SettingsGroup[], ctx: SettingsAccessContext): SettingsGroup[] {
  return groups
    .map((group) => ({ ...group, items: filterVisibleSettingsItems(group.items, ctx) }))
    .filter((group) => {
      if (group.adminOnly && !ctx.isPlatformAdmin && group.items.length === 0) return false;
      return group.items.length > 0;
    });
}

export function hasAnyAccessibleSettings(ctx: SettingsAccessContext): boolean {
  return ALL_SETTINGS_GROUPS.some((group) => {
    return group.items.some((item) => isSettingsItemVisible(item, ctx));
  });
}
